import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import { getProjects } from '../lib/cmsClient';

// De 6 autentiske referenceprojekter fra dansktagbyg.dk
const PROJECTS_DATA = [
  {
    id: 'eternittag-paa-sjaelland',
    category: 'Tag',
    title: 'Eternittag på Sjælland',
    description: 'For en kunde på Sjælland stod vi for at fjerne og bortskaffe det gamle betontag samt lægge nyt eternittag B7.',
    location: 'Sjælland',
    image: '/_assets/01_0e9d7fdf.jpg',
    link: '/projekter'
  },
  {
    id: 'eternittag-i-sabro',
    category: 'Tag',
    title: 'Eternittag i Sabro',
    description: 'For en kunde udførte vi udskiftning af et gammelt asbesttag med et nyt eternittag B6 samt opbygning af stillads.',
    location: 'Sabro',
    image: '/_assets/01_bde89f58.jpg',
    link: '/projekter'
  },
  {
    id: 'nye-vinduer-i-aabyhoej',
    category: 'Energiløsninger',
    title: 'Nye vinduer i Åbyhøj',
    description: 'I en klassisk murermestervilla i Åbyhøj har vi udskiftet 10 vinduer med komplet finish både indvendigt og udvendigt.',
    location: 'Åbyhøj',
    image: '/_assets/01_6c362393.jpg',
    link: '/projekter'
  },
  {
    id: 'betontag-i-skanderborg',
    category: 'Tag',
    title: 'Betontag i Skanderborg',
    description: 'For en kunde i Skanderborg udførte vi en komplet tagrenovering med betontag, loftsisolering, nye vindskeder og zink.',
    location: 'Skanderborg',
    image: '/_assets/01.jpg',
    link: '/projekter'
  },
  {
    id: 'betontag-i-silkeborg',
    category: 'Tag',
    title: 'Betontag i Silkeborg',
    description: 'For en kunde i Silkeborg lagde vi et helt nyt betontag, hvor der i samme omgang blev efterisoleret på loftet.',
    location: 'Silkeborg',
    image: '/_assets/01_daece9cc.jpg',
    link: '/projekter'
  },
  {
    id: 'eternittag-i-randers',
    category: 'Tag',
    title: 'Eternittag i Randers',
    description: 'En kunde i Randers fik lagt et nyt flot eternittag, der er både miljøvenligt, modstandsdygtigt og vedligeholdelsesfrit.',
    location: 'Randers',
    image: '/_assets/01_3a438c12.jpg',
    link: '/projekter'
  }
];

// De 6 autentiske kundeudtalelser fra dansktagbyg.dk
const TESTIMONIALS_DATA = [
  {
    quote: "Aftaler og pris er overholdt. Nem at komme i kontakt med under hele forløbet. Der har været god kommunikation med håndværkerne og de har udført et yderst professionelt stykke arbejde som vi vil nyde i årene fremover. Finish og oprydning kendetegner firmaet.",
    author: "Nils Schiønnemann, Galten"
  },
  {
    quote: "Allerede ved første besøg var det en god oplevelse, god forståelse for vores ønsker og konstruktive forslag til løsninger vi ikke havde tænkt på... God og hurtig kommunikation fra modtagelse af tilbud til accept.",
    author: "Lene & Ole Zahrtmann, Brabrand"
  },
  {
    quote: "Vi havde en rigtig god fornemmelse da Steffen var ude at give tilbud, og det holdt stik da Lasse og hans hold kom og skiftede vores tag på kun 5 dage. De var søde og imødekomne og meget effektive. Og så ryddede de pænt op efter sig hver dag.",
    author: "Nicolas Serval, Roskilde"
  },
  {
    quote: "Midtjyllands allerbedste priser, til den bedste kvalitet. Lasse er ærlig, imødekommende og det er hurtigt tydeligt, at han ved, hvad han taler om. Vi kunne ikke være mere tilfreds med slutresultatet.",
    author: "David Gélie, Langå"
  },
  {
    quote: "Jeg fik udskiftet mit tag, tagrender, vindskeder og isat et nyt Velux vindue på blot fem dage. Jeg ønskede ikke at risikere et hus pakket ind i stilladser i månedsvis, fordi en evt. anden håndværker også havde andre opgaver ind i mellem.",
    author: "Klaus Rebien, Ringsted"
  },
  {
    quote: "Fik udskiftet et gammelt tegltag og sat nye Velux vinduer i. Behagelige mennesker at omgås hele vejen igennem, hjælpsomme med råd og vejledning fra start til slut. Tidsplanen blev overholdt, faktisk blev der startet en uge før oprindeligt planlagt.",
    author: "Henrik Jeppesen, Aarhus N"
  }
];

export default function HomePage() {
  const [projectsList, setProjectsList] = useState(PROJECTS_DATA);
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [openRoofFaq, setOpenRoofFaq] = useState(null); // Alle FAQ er lukkede som standard
  const [openEnergyFaq, setOpenEnergyFaq] = useState(null);

  useEffect(() => {
    async function load() {
      const data = await getProjects();
      if (data && data.length > 0) {
        setProjectsList(data.map(p => ({
          ...p,
          link: '/projekter'
        })));
      }
    }
    load();
  }, []);

  // Draggable Carousel State & Handlers
  const carouselContainerRef = useRef(null);
  const carouselViewportRef = useRef(null);
  const carouselTrackRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [currentTranslate, setCurrentTranslate] = useState(0);
  const [maxScroll, setMaxScroll] = useState(0);
  const [isHoveringCarousel, setIsHoveringCarousel] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const handleViewportMouseMove = (e) => {
    if (carouselViewportRef.current) {
      const rect = carouselViewportRef.current.getBoundingClientRect();
      setMousePos({ 
        x: e.clientX - rect.left, 
        y: e.clientY - rect.top 
      });
    }
    if (!isHoveringCarousel) setIsHoveringCarousel(true);
  };

  // Refs til lagfri direkte DOM-manipulation og momentum
  const isPointerDownRef = useRef(false);
  const startXRef = useRef(0);
  const startTranslateRef = useRef(0);
  const currentTranslateRef = useRef(0);
  const hasDraggedRef = useRef(false);
  const dragEndTimeRef = useRef(0);
  const lastXRef = useRef(0);
  const lastTimeRef = useRef(0);
  const velocityRef = useRef(0);
  const maxScrollRef = useRef(0);
  const activePointerIdRef = useRef(null);

  // Beregn præcise grænser for horisontal trækning
  const updateBounds = useCallback(() => {
    if (carouselTrackRef.current && (carouselViewportRef.current || carouselContainerRef.current)) {
      const trackWidth = carouselTrackRef.current.scrollWidth;
      const viewportWidth = carouselViewportRef.current 
        ? carouselViewportRef.current.offsetWidth 
        : carouselContainerRef.current.offsetWidth;
      const maxDist = Math.max(0, trackWidth - viewportWidth);
      const newMax = -maxDist;
      setMaxScroll(newMax);
      maxScrollRef.current = newMax;

      // Justér translate hvis bredden har ændret sig
      if (currentTranslateRef.current < newMax) {
        currentTranslateRef.current = newMax;
        setCurrentTranslate(newMax);
        if (carouselTrackRef.current) {
          carouselTrackRef.current.style.transform = `translate3d(${newMax}px, 0, 0)`;
        }
      }
    }
  }, []);

  useEffect(() => {
    updateBounds();
    window.addEventListener('resize', updateBounds);
    const ro = new ResizeObserver(updateBounds);
    if (carouselViewportRef.current) ro.observe(carouselViewportRef.current);
    if (carouselTrackRef.current) ro.observe(carouselTrackRef.current);

    return () => {
      window.removeEventListener('resize', updateBounds);
      ro.disconnect();
    };
  }, [updateBounds]);

  const handlePointerDown = (e) => {
    // Kun venstre museknap eller touch
    if (e.button !== undefined && e.button !== 0 && e.pointerType === 'mouse') return;

    isPointerDownRef.current = true;
    activePointerIdRef.current = e.pointerId;
    hasDraggedRef.current = false;
    startXRef.current = e.clientX;
    lastXRef.current = e.clientX;
    lastTimeRef.current = performance.now();
    startTranslateRef.current = currentTranslateRef.current;
    velocityRef.current = 0;

    // Fjern transition under aktiv trækning for 1:1 responsivitet
    if (carouselTrackRef.current) {
      carouselTrackRef.current.style.transition = 'none';
    }

    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {}

    setIsDragging(true);
  };

  const handlePointerMove = (e) => {
    if (!isPointerDownRef.current) return;

    const currentX = e.clientX;
    const diff = currentX - startXRef.current;

    // Hvis cursor har bevæget sig mere end 4px, tæller det som drag (og forhindrer utilsigtet klik)
    if (Math.abs(diff) > 4) {
      hasDraggedRef.current = true;
    }

    // Beregn bevægelseshastighed for naturlig inertia/momentum
    const now = performance.now();
    const dt = Math.max(1, now - lastTimeRef.current);
    const dx = currentX - lastXRef.current;
    velocityRef.current = dx / dt;
    lastXRef.current = currentX;
    lastTimeRef.current = now;

    let targetTranslate = startTranslateRef.current + diff;
    const max = maxScrollRef.current;

    // Elastisk modstand ved ydergrænser
    if (targetTranslate > 0) {
      targetTranslate = targetTranslate * 0.25;
    } else if (targetTranslate < max) {
      targetTranslate = max + (targetTranslate - max) * 0.25;
    }

    currentTranslateRef.current = targetTranslate;

    // Direkte transformering uden re-render for perfekt 60/120 fps flydende bevægelse
    if (carouselTrackRef.current) {
      carouselTrackRef.current.style.transform = `translate3d(${targetTranslate}px, 0, 0)`;
    }
  };

  const handlePointerUp = (e) => {
    if (!isPointerDownRef.current) return;
    isPointerDownRef.current = false;
    setIsDragging(false);

    try {
      if (activePointerIdRef.current !== null && e?.currentTarget) {
        e.currentTarget.releasePointerCapture(activePointerIdRef.current);
      }
    } catch {}

    const max = maxScrollRef.current;
    // Tilføj momentum baseret på hastighed
    let momentum = velocityRef.current * 180;
    if (Math.abs(momentum) > 550) {
      momentum = Math.sign(momentum) * 550;
    }

    let finalTranslate = currentTranslateRef.current + momentum;
    if (finalTranslate > 0) finalTranslate = 0;
    if (finalTranslate < max) finalTranslate = max;

    currentTranslateRef.current = finalTranslate;
    setCurrentTranslate(finalTranslate);

    // Blød glide-animation til slutposition
    if (carouselTrackRef.current) {
      carouselTrackRef.current.style.transition = 'transform 0.45s cubic-bezier(0.16, 1, 0.3, 1)';
      carouselTrackRef.current.style.transform = `translate3d(${finalTranslate}px, 0, 0)`;
    }

    if (hasDraggedRef.current) {
      dragEndTimeRef.current = Date.now();
    }

    // Nulstil hasDragged efter kort delay, så evt. click-event kan nå at blive afvist
    setTimeout(() => {
      hasDraggedRef.current = false;
    }, 300);
  };

  const handleLinkClick = (e) => {
    if (hasDraggedRef.current || Date.now() - dragEndTimeRef.current < 250) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const preventNativeDrag = (e) => {
    e.preventDefault();
  };

  const getStepWidth = () => {
    if (carouselTrackRef.current && carouselTrackRef.current.firstElementChild) {
      return carouselTrackRef.current.firstElementChild.offsetWidth + 27;
    }
    return 350;
  };

  const scrollPrev = () => {
    const step = getStepWidth();
    const next = Math.min(0, currentTranslateRef.current + step);
    currentTranslateRef.current = next;
    setCurrentTranslate(next);
    if (carouselTrackRef.current) {
      carouselTrackRef.current.style.transition = 'transform 0.45s cubic-bezier(0.16, 1, 0.3, 1)';
      carouselTrackRef.current.style.transform = `translate3d(${next}px, 0, 0)`;
    }
  };

  const scrollNext = () => {
    const step = getStepWidth();
    const max = maxScrollRef.current;
    const next = Math.max(max, currentTranslateRef.current - step);
    currentTranslateRef.current = next;
    setCurrentTranslate(next);
    if (carouselTrackRef.current) {
      carouselTrackRef.current.style.transition = 'transform 0.45s cubic-bezier(0.16, 1, 0.3, 1)';
      carouselTrackRef.current.style.transform = `translate3d(${next}px, 0, 0)`;
    }
  };

  useEffect(() => {
    AOS.refresh();
  }, [quoteIndex, openRoofFaq, openEnergyFaq]);

  // Udtalelser navigation
  const prevQuote = () => {
    setQuoteIndex((prev) => (prev > 0 ? prev - 1 : TESTIMONIALS_DATA.length - 1));
  };

  const nextQuote = () => {
    setQuoteIndex((prev) => (prev < TESTIMONIALS_DATA.length - 1 ? prev + 1 : 0));
  };

  const toggleRoofFaq = (index) => {
    setOpenRoofFaq((prev) => (prev === index ? null : index));
  };

  const toggleEnergyFaq = (index) => {
    setOpenEnergyFaq((prev) => (prev === index ? null : index));
  };

  return (
    <div className="w-full bg-white text-[#2E2E2E] overflow-x-hidden">

      {/* ============================================================ */}
      {/* 2. LARGE HERO SECTION WITH DARK IMAGE BACKGROUND (100VH)     */}
      {/* ============================================================ */}
      <section 
        id="hero"
        className="relative w-full min-h-[100dvh] lg:h-screen flex flex-col justify-between overflow-hidden bg-black text-white"
      >
        {/* Fuld bredde baggrund: Video med poster-fallback og 60% mørk sort overlay */}
        <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
          <video
            autoPlay
            muted
            loop
            playsInline
            poster="/_assets/scraped-1_d2f93850.webp"
            className="w-full h-full object-cover object-center"
          >
            <source src="/Video/teaser-xlarge.m4v" type="video/mp4" />
            <img
              src="/_assets/scraped-1_d2f93850.webp"
              alt="Dansk Tagbyg tagkonstruktion"
              className="w-full h-full object-cover object-center"
            />
          </video>
          {/* Kraftigt sort overlay ca. 70% (+5% darkness) */}
          <div className="absolute inset-0 bg-black/70" />
        </div>

        {/* Hovedoverskrift: reduceret ~3% til 68px, flyttet 2px UP til lg:top-[calc(30vh+3.5px)] -translate-x-[3.5px], præcis 3 linjer */}
        <div 
          className="relative z-10 pt-28 sm:pt-36 lg:pt-0 lg:absolute lg:top-[calc(30vh+3.5px)] px-5 sm:px-6 lg:px-0 w-full lg:w-[760px] max-w-[760px] lg:-translate-x-[3.5px] hero-heading-pos"
          data-aos="fade-up" 
          data-aos-duration="600"
        >
          <h1 className="font-['Saira'] font-bold text-[30px] xs:text-[36px] sm:text-[48px] lg:text-[62px] xl:text-[68px] text-white tracking-tight leading-[1.08] sm:leading-[1.05] mb-0 whitespace-normal">
            Eksperter i tag- og<br className="hidden sm:inline" />{" "}
            energiløsninger for<br className="hidden sm:inline" />{" "}
            private og erhverv
          </h1>
        </div>

        {/* 3 Sammenhængende handlingsområder forankret i bunden af heroen (30% / 27% / 43% split) */}
        <div className="relative z-20 w-full lg:absolute lg:bottom-0 lg:left-0 lg:right-0 lg:h-[30vh] lg:min-h-[225px] lg:max-h-[295px]" data-aos="fade-up" data-aos-duration="600">
          <div className="w-full h-full flex flex-col md:flex-row items-stretch">
            
            {/* Tom spacer venstre: x=0 til indholdslinje på desktop */}
            <div 
              className="hidden lg:block shrink-0 pointer-events-none" 
              style={{ width: 'calc(max(19vw, calc((100vw - 1180px) / 2)) - 4px)' }}
            />

            {/* Handlingsområde spænder fra indholdslinje til højre viewport-kant */}
            <div className="flex-1 h-full flex flex-col md:flex-row items-stretch">
              
              {/* Område 1: Skillelinje flyttet ~13px LEFT (calc(30% - 38px)) */}
              <Link
                to="/nyt-tag"
                className="w-full md:w-[calc(30%-38px)] shrink-0 h-full transition-colors pt-6 pb-6 pr-5 pl-5 lg:pl-[98px] lg:pr-6 flex flex-col justify-center border-t border-white/15 text-white no-underline group rounded-none relative"
                style={{ backgroundColor: 'rgba(0, 0, 0, 0.68)' }}
              >
                {/* Kortere, diskret lodret separator i tekstområdet */}
                <div className="hidden md:block absolute right-0 top-[18%] bottom-[18%] w-[1px] bg-white/15 pointer-events-none" />
                
                <div>
                  <h3 className="font-['Saira'] font-bold text-[22px] sm:text-[23px] lg:text-[24px] text-white mb-2 tracking-wide leading-snug text-left">
                    Nyt tag?
                  </h3>
                  <p className="font-['Open_Sans'] font-normal text-[14px] sm:text-[14.5px] text-white/80 leading-relaxed mb-3.5 text-left max-w-[230px]">
                    Se, hvordan vi kan hjælpe dig med dit nye tag eller kontakt os for at høre mere.
                  </p>
                  <div className="flex items-center justify-start text-white/90 group-hover:text-white group-hover:translate-x-1.5 transition-all text-sm">
                    <i className="fa-solid fa-arrow-right"></i>
                  </div>
                </div>
              </Link>

              {/* Område 2: Skillelinje flyttet 13px LEFT til venstre og 9px LEFT til højre (calc(27% + 29px) -> udvides med 4.5px til calc(27% + 33.5px)) */}
              <Link
                to="/energiloesninger"
                className="w-full md:w-[calc(27%+33.5px)] shrink-0 h-full transition-colors pt-6 pb-6 pr-5 pl-5 lg:pl-[101px] lg:pr-6 flex flex-col justify-center border-t border-white/15 text-white no-underline group rounded-none relative"
                style={{ backgroundColor: 'rgba(0, 0, 0, 0.68)' }}
              >
                {/* Kortere, diskret lodret separator i tekstområdet */}
                <div className="hidden md:block absolute right-0 top-[18%] bottom-[18%] w-[1px] bg-white/15 pointer-events-none" />

                <div>
                  <h3 className="font-['Saira'] font-bold text-[22px] sm:text-[23px] lg:text-[24px] text-white mb-2 tracking-wide leading-snug text-left">
                    Energiløsninger?
                  </h3>
                  <p className="font-['Open_Sans'] font-normal text-[14px] sm:text-[14.5px] text-white/80 leading-relaxed mb-3.5 text-left max-w-[230px]">
                    Se her, hvordan vi kan hjælpe dig og beregn en ca. pris på isolering af dit tag.
                  </p>
                  <div className="flex items-center justify-start text-white/90 group-hover:text-white group-hover:translate-x-1.5 transition-all text-sm">
                    <i className="fa-solid fa-arrow-right"></i>
                  </div>
                </div>
              </Link>

              {/* Panel 3 / Hero bund: Rødt prisberegnerpanel (starter 9px længere mod venstre og fortsætter til højre viewport-kant) */}
              <div
                className="w-full md:flex-1 h-full pt-[28px] pb-[28px] pl-[28px] lg:pl-[32px] pr-[20px] lg:pr-[24px] flex flex-col md:flex-row items-center justify-between border-t border-white/15 text-white rounded-none relative group"
                style={{ backgroundColor: 'rgba(195, 0, 5, 0.86)' }}
              >
                {/* Venstre halvdel: Prisberegner-indhold */}
                <Link
                  to="/prisberegner"
                  className="flex-1 flex flex-col justify-center text-white no-underline z-10 text-left translate-x-0 lg:translate-x-[21px]"
                >
                  <h3 className="font-['Saira'] font-bold text-[22px] sm:text-[24px] lg:text-[26px] text-white mb-2 tracking-wide leading-[1.15] text-left max-w-[190px]">
                    Prøv vores<br />
                    prisberegner<br />
                    til tag
                  </h3>
                  <p className="font-['Open_Sans'] font-normal text-[13px] sm:text-[13.5px] text-white/90 leading-relaxed mb-3 text-left max-w-[240px]">
                    Beregn et uforpligtende estimat på dit nye tag på under 2 minutter.
                  </p>
                  <div className="flex items-center justify-start text-white group-hover:translate-x-1.5 transition-all text-sm">
                    <i className="fa-solid fa-arrow-right"></i>
                  </div>
                </Link>
              </div>
            </div>

          </div>
        </div>

      </section>

      {/* ============================================================ */}
      {/* 3. INTRODUCTORY TRUST SECTION (top-padding reduceret med 11px fra pt-[126px] til pt-[115px]) */}
      {/* ============================================================ */}
      <section id="tillid" className="pt-[115px] pb-0 bg-white">
        <div 
          className="w-full mx-auto px-4 sm:px-6 lg:px-0"
          style={{ width: 'clamp(820px, 62vw, 1180px)', maxWidth: '100%' }}
        >
          <h2 className="font-['Saira'] font-bold text-[28px] sm:text-[35px] lg:text-[38px] text-[#2E2E2E] tracking-tight leading-[1.15] mb-4 text-left">
            Fagfolk, som du kan stole på
          </h2>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            {/* Brødtekst: læsevenlig bredde, 2 afsnit, øget skrift ca. 10-12% */}
            <div className="w-full max-w-[520px] space-y-3 font-['Open_Sans'] text-[16.5px] sm:text-[17px] text-[#363636] leading-[1.7] text-left">
              <p>
                Et nyt tag er en stor beslutning, der på sigt dog er en investering i din bolig og trivsel. Det er derfor vigtigt, at du fra start får hjælp af pålidelige og dygtige fagfolk, som kan sørge for, at dit tag når nye højder.
              </p>
              <p className="mb-0">
                Hos Dansk Tagbyg er vi specialister i alle typer tag, og vi kører ud til private og erhvervskunder i hele Danmark. Vi vægter den gode relation med kunden højt og holder altid, hvad vi lover, så du med ro i maven kan betro os med dit næste tagprojekt.
              </p>
            </div>

            {/* CTA: forstørret rød pilleknap bredde +20-25%, højde +5-8%, flyttet ~12-15px VENSTRE */}
            <div className="flex items-center gap-3 -translate-y-2.5 sm:-translate-y-3 -translate-x-[14px] shrink-0">
              <Link
                to="/projekter"
                className="rounded-full bg-[#BE0F12] hover:bg-[#A30D0F] text-white px-10 sm:px-11 py-4 text-[14.5px] sm:text-[15.5px] font-semibold transition-colors inline-block text-center shadow-xs"
              >
                Se udvalgte projekter
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* 4. PROJECT / CASE SHOWCASE SECTION (DRAGGABLE CAROUSEL)      */}
      {/* ============================================================ */}
      <section id="projekter" className="pt-[105px] pb-[118px] bg-white overflow-x-clip relative -translate-y-[5px] group/showcase">
        <div 
          ref={carouselContainerRef}
          className="w-full mx-auto px-4 sm:px-6 lg:px-0 relative"
          style={{ width: 'clamp(820px, 62vw, 1180px)', maxWidth: '100%' }}
        >

          {/* 
            Carousel Viewport:
            På desktop viser den præcis Projekt 1 (100%), Projekt 2 (100%) og præcis ca. 45% af Projekt 3.
            Viewport udvidet ~20-25px mod højre (+88px mod tidligere +65px).
            Kortbredden: ((containerWidth - 27px) / 2) * 0.987 (+1-1.5% bredde)
            Mellemrum mellem kort: 27px (+2px).
          */}
          <div 
            ref={carouselViewportRef}
            className="w-full lg:w-[calc((((clamp(820px,62vw,1180px)-27px)/2)*0.987)*2.45+88px)] overflow-hidden relative group/carousel"
          >
            {/* Hvid fade gradient på højre kant (starter en smule senere, 150px) */}
            <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-32 sm:w-40 lg:w-[150px] bg-gradient-to-l from-white from-20% via-white/80 via-65% to-transparent z-10" />

            {/* Trækbar karrusel-wrapper med jævn Pointer Events drag og grab/grabbing cursor */}
            <div 
              className={`w-full overflow-visible touch-pan-y select-none transition-colors ${isDragging ? 'cursor-grabbing select-none' : 'cursor-grab'}`}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              onDragStart={preventNativeDrag}
            >
              {/* Draggable Track med hardware acceleration og 27px gap (+2px) */}
              <div 
                ref={carouselTrackRef}
                className="flex items-start select-none"
                onDragStart={preventNativeDrag}
                style={{ 
                  gap: '27px',
                  transform: `translate3d(${currentTranslate}px, 0, 0)`,
                  transition: isDragging ? 'none' : 'transform 0.45s cubic-bezier(0.16, 1, 0.3, 1)'
                }}
              >
                {projectsList.map((project) => {
                  return (
                    <div
                      key={project.id}
                      className="group flex flex-col shrink-0 w-[278px] sm:w-[325px] lg:w-[calc(((clamp(820px,62vw,1180px)-27px)/2)*0.987)] relative select-none"
                      onDragStart={preventNativeDrag}
                    >
                      {/* Billede: Clean rektangulært med ~2-3% øget højde (aspect ratio 1.61/1) og object-fit: cover */}
                      <div 
                        className={`w-full overflow-hidden bg-neutral-100 relative ${isDragging ? 'cursor-grabbing' : 'cursor-grab'} select-none`}
                        style={{ aspectRatio: '1.61 / 1' }}
                      >
                        <Link 
                          to={project.link} 
                          draggable={false}
                          onDragStart={preventNativeDrag}
                          onClick={handleLinkClick}
                          className={`block w-full h-full ${isDragging ? 'cursor-grabbing' : 'cursor-grab'} select-none`}
                        >
                          <img
                            src={project.image}
                            alt={project.title}
                            loading="lazy"
                            draggable={false}
                            onDragStart={preventNativeDrag}
                            className="w-full h-full object-cover group-hover:scale-103 transition-transform duration-500 pointer-events-none select-none"
                          />
                        </Link>
                      </div>
                      
                      {/* Tekst under billedet - 5-delt struktur med specificeret vertikal rytme */}
                      <div className="pt-3.5 flex flex-col flex-grow select-none">
                        {/* 1. Lille mørk kategoripille */}
                        <div className="mb-1">
                          <span className="inline-block bg-[#2E2E2E] text-white text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full tracking-wider">
                            {project.category}
                          </span>
                        </div>

                        {/* 2. Bold projekttitel */}
                        <h3 className="font-['Saira'] font-bold text-[19px] sm:text-[21px] lg:text-[22px] text-[#2E2E2E] mb-[5px] leading-snug group-hover:text-[#BE0F12] transition-colors">
                          <Link 
                            to={project.link} 
                            draggable={false}
                            onDragStart={preventNativeDrag}
                            onClick={handleLinkClick}
                            className="hover:text-[#BE0F12]"
                          >
                            {project.title}
                          </Link>
                        </h3>

                        {/* 3. Kort projektbeskrivelse */}
                        <p className="font-['Open_Sans'] text-[15.5px] sm:text-[16.5px] text-[#4A4A4A] leading-[1.7] mb-[7px] flex-grow">
                          {project.description}
                        </p>

                        {/* 4. Lille neutral projekt-information/meta linje (ikke rød location-pin) */}
                        <div className="text-[12.5px] sm:text-[13px] text-neutral-600 mb-[7px] font-medium tracking-wide">
                          <span>{project.location}</span>
                        </div>

                        {/* 5. "Se hele projektet →" tekstlink */}
                        <Link
                          to={project.link}
                          draggable={false}
                          onDragStart={preventNativeDrag}
                          onClick={handleLinkClick}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2E2E2E] group-hover:text-[#BE0F12] transition-colors mt-auto"
                        >
                          <span>Se hele projektet</span>
                          <i className="fa-solid fa-arrow-right text-[10px] text-[#BE0F12] group-hover:translate-x-1 transition-transform"></i>
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ============================================================ */}
      {/* 5. TESTIMONIAL / REVIEW HIGHLIGHT SECTION                    */}
      {/* ============================================================ */}
      <section id="anmeldelser" className="pt-14 md:pt-20 lg:pt-[93px] pb-10 lg:pb-12 bg-white overflow-visible -mt-[20px] mb-[47px]">
        <div 
          className="w-full mx-auto px-4 sm:px-6 lg:px-0 overflow-visible"
          style={{ width: 'calc(clamp(820px, 62vw, 1180px) * 0.99)', maxWidth: '100%' }}
        >
          {/* Mørkt rektangel: bg #383838, højde 517px (+8px), rykket 2-3px højre */}
          <div className="relative bg-[#383838] text-white w-full min-h-[480px] md:h-[508px] lg:h-[517px] rounded-none shadow-none border-0 overflow-visible flex flex-col md:flex-row items-center translate-x-[2.5px]">
            
            {/* Venstre område: ca. 50% af boksen, Trofæ forstørret 10-12% (630px), flyttet 24px RIGHT til left-[112px] */}
            <div className="w-full md:w-[50%] lg:w-[50%] h-[480px] md:h-[508px] lg:h-[517px] relative flex-shrink-0 overflow-visible flex items-end justify-center md:justify-start">
              <img
                src="/_assets/imgi-7-thumb.png"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = 'https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/imgi-7-thumb.png';
                }}
                alt="Årets Håndværker bronze statuette"
                loading="lazy"
                className="h-[545px] md:h-[590px] lg:h-[630px] w-auto max-w-none object-contain absolute left-[112px] md:left-[112px] -top-[58px] lg:-top-[65px] z-10 filter drop-shadow-md pointer-events-none"
              />
              
              {/* HVID cirkulær finalist badge: forstørret ~15% (w-[224px] h-[224px]), behold venstre kant (left-[36px]), flyttet 8px DOWN til bottom-[52px] */}
              <div 
                className="absolute left-[36px] md:left-[36px] bottom-[44px] lg:bottom-[52px] w-[190px] h-[190px] lg:w-[224px] lg:h-[224px] rounded-full bg-white text-[#383838] p-4 flex flex-col items-center justify-center text-center shadow-2xl border border-neutral-200 z-20"
                style={{ transform: 'rotate(-30deg)' }}
              >
                <span className="font-['Saira'] font-bold text-[13.5px] sm:text-[14px] lg:text-[15px] uppercase tracking-wider leading-snug text-[#383838]">
                  Finalist til<br />Årets Håndværker<br />5 år i træk
                </span>
              </div>
            </div>

            {/* Højre halvdel: Anmeldelsestekst optager ca. 50% */}
            <div className="w-full md:w-[50%] lg:w-[50%] flex-1 h-full relative flex flex-col justify-center px-6 sm:px-8 py-8 md:py-0 md:pr-10 md:pl-0 -translate-x-[6px] -translate-y-[6px]">
              
              {/* Venstre pil ved venstre kant af tekstområdet */}
              <button
                type="button"
                onClick={prevQuote}
                aria-label="Forrige anmeldelse"
                className="absolute left-1 md:left-0 top-1/2 -translate-y-1/2 w-8 h-8 text-white/70 hover:text-white flex items-center justify-center transition-colors cursor-pointer z-10"
              >
                <i className="fa-solid fa-chevron-left text-lg text-white"></i>
              </button>

              {/* Højre pil ved højre kant */}
              <button
                type="button"
                onClick={nextQuote}
                aria-label="Næste anmeldelse"
                className="absolute right-1 md:right-3 top-1/2 -translate-y-1/2 w-8 h-8 text-white/70 hover:text-white flex items-center justify-center transition-colors cursor-pointer z-10"
              >
                <i className="fa-solid fa-chevron-right text-lg text-white"></i>
              </button>

              <div className="px-6 md:px-8">
                {/* Forøget overskrift ~10% */}
                <h2 className="font-['Saira'] font-bold text-[26px] sm:text-[28px] lg:text-[31px] text-white tracking-tight leading-snug mb-4">
                  Vi motiveres af glade kunder og gode resultater
                </h2>

                <div className="min-h-[110px] mb-4">
                  <p className="font-['Open_Sans'] font-light italic text-[16px] sm:text-[17px] text-neutral-200 leading-[1.7] mb-2.5">
                    "{TESTIMONIALS_DATA[quoteIndex].quote}"
                  </p>
                  <p className="font-['Saira'] font-semibold text-sm sm:text-[15px] text-white tracking-wide">
                    {TESTIMONIALS_DATA[quoteIndex].author}
                  </p>
                </div>

                {/* Stjernerække med HVIDE stjerner og anmeldelsesscore */}
                <div className="pt-4 border-t border-white/15 flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-1 text-white text-base">
                    <i className="fa-solid fa-star"></i>
                    <i className="fa-solid fa-star"></i>
                    <i className="fa-solid fa-star"></i>
                    <i className="fa-solid fa-star"></i>
                    <i className="fa-solid fa-star"></i>
                  </div>
                  <a
                    href="https://www.anmeld-haandvaerker.dk/tagdaekker/dansk-tagbyg-aps"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-['Saira'] font-medium text-[13px] sm:text-[13.5px] text-neutral-300 hover:text-white transition-colors"
                  >
                    4,8 ud af 155 anmeldelser på Anmeld Håndværker
                  </a>
                </div>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* 6. LARGE FAQ / INFORMATION SECTION (62vw, 2 KOLONNER)        */}
      {/* ============================================================ */}
      <section id="faq" className="pt-8 pb-7 lg:pt-[34px] lg:pb-[19px] bg-white">
        <div 
          className="w-full mx-auto px-4 sm:px-6 lg:px-0"
          style={{ width: 'clamp(820px, 62vw, 1180px)', maxWidth: '100%' }}
        >
          
          {/* Venstrestillet stor overskrift i 2 linjer */}
          <div className="mb-8">
            <h2 className="font-['Saira'] font-bold text-[28px] sm:text-[35px] lg:text-[38px] text-[#2E2E2E] tracking-tight leading-tight">
              Hvorfor investere i nyt tag<br />eller energiløsninger?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 items-start" style={{ gap: '3%' }}>
            
            {/* Kolonne 1: Tag (48.5% bredde) - distance til første række øget ~4px (mb-5.5) */}
            <div className="w-full">
              <h3 className="font-['Saira'] font-bold text-base sm:text-lg text-[#2E2E2E] mb-5.5 pb-1 border-b border-neutral-200">
                Tag
              </h3>

              <div className="space-y-[6px]">
                
                {/* Tag FAQ 1 - højde øget ~2px (min-h-[74px]) */}
                <div className="bg-[#EDEDED] border border-neutral-300/60 transition-colors">
                  <button
                    type="button"
                    onClick={() => toggleRoofFaq(0)}
                    className="w-full py-5 min-h-[74px] text-left px-5 sm:px-6 text-[#2E2E2E] font-['Saira'] font-semibold text-[15px] sm:text-[16px] lg:text-[16.5px] flex items-center justify-between gap-3 cursor-pointer hover:text-[#BE0F12] transition-colors whitespace-normal"
                  >
                    <span className="pr-2 leading-snug">Hvad koster et nyt tag?</span>
                    <i className={`fa-solid fa-chevron-down text-[13px] shrink-0 transition-transform duration-200 ${openRoofFaq === 0 ? 'rotate-180 text-[#BE0F12]' : 'text-neutral-500'}`}></i>
                  </button>
                  {openRoofFaq === 0 && (
                    <div className="px-4 pb-4 pt-1 font-['Open_Sans'] text-[13px] sm:text-[13.5px] text-[#363636] space-y-3 leading-relaxed border-t border-neutral-300 bg-white">
                      <p className="pt-2">
                        Prisen på et nyt tag varierer afhængigt af valg af tagmateriale, tagets størrelse, hældning og kompleksitet samt undertag og isolering. Se vejledende priseksempler nedenfor:
                      </p>
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs border border-neutral-200">
                          <thead className="bg-[#2E2E2E] text-white">
                            <tr>
                              <th className="p-2">Tagtype</th>
                              <th className="p-2">Ca. pris pr. m²</th>
                              <th className="p-2">Forventet levetid</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-neutral-200">
                            <tr>
                              <td className="p-2 font-semibold">Betontagsten</td>
                              <td className="p-2">800 - 1.200 kr.</td>
                              <td className="p-2">40 - 50 år</td>
                            </tr>
                            <tr>
                              <td className="p-2 font-semibold">Tegltag</td>
                              <td className="p-2">1.200 - 1.800 kr.</td>
                              <td className="p-2">50 - 70 år</td>
                            </tr>
                            <tr>
                              <td className="p-2 font-semibold">Tagpap</td>
                              <td className="p-2">700 - 1.100 kr.</td>
                              <td className="p-2">30 - 40 år</td>
                            </tr>
                            <tr>
                              <td className="p-2 font-semibold">Ståltag</td>
                              <td className="p-2">600 - 1.000 kr.</td>
                              <td className="p-2">30 - 50 år</td>
                            </tr>
                            <tr>
                              <td className="p-2 font-semibold">Naturskifer</td>
                              <td className="p-2">1.500 - 2.500 kr.</td>
                              <td className="p-2">80 - 100+ år</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <p className="text-xs text-neutral-500 italic mt-2">
                        * Alle priser er vejledende og ekskl. eventuel asbestsanering, stillads og ekstra tømrerarbejde.
                      </p>
                    </div>
                  )}
                </div>

                {/* Tag FAQ 2 */}
                <div className="bg-[#EDEDED] border border-neutral-300/60 transition-colors">
                  <button
                    type="button"
                    onClick={() => toggleRoofFaq(1)}
                    className="w-full py-5 min-h-[74px] text-left px-5 sm:px-6 text-[#2E2E2E] font-['Saira'] font-semibold text-[15px] sm:text-[16px] lg:text-[16.5px] flex items-center justify-between gap-3 cursor-pointer hover:text-[#BE0F12] transition-colors whitespace-normal"
                  >
                    <span className="pr-2 leading-snug">Hvilken type tag er den bedste til min bolig?</span>
                    <i className={`fa-solid fa-chevron-down text-[13px] shrink-0 transition-transform duration-200 ${openRoofFaq === 1 ? 'rotate-180 text-[#BE0F12]' : 'text-neutral-500'}`}></i>
                  </button>
                  {openRoofFaq === 1 && (
                    <div className="px-4 pb-4 pt-1 font-['Open_Sans'] text-[13px] sm:text-[13.5px] text-[#363636] space-y-2 leading-relaxed border-t border-neutral-300 bg-white">
                      <p className="pt-2">
                        Det afhænger af din boligs arkitektur, tagets hældning, lokalplaner og dit budget. Tegl og beton er populære til klassiske villaer, mens ståltag og tagpap egner sig til lavere hældninger. Vi rådgiver dig gerne om det optimale valg.
                      </p>
                    </div>
                  )}
                </div>

                {/* Tag FAQ 3 */}
                <div className="bg-[#EDEDED] border border-neutral-300/60 transition-colors">
                  <button
                    type="button"
                    onClick={() => toggleRoofFaq(2)}
                    className="w-full py-5 min-h-[74px] text-left px-5 sm:px-6 text-[#2E2E2E] font-['Saira'] font-semibold text-[15px] sm:text-[16px] lg:text-[16.5px] flex items-center justify-between gap-3 cursor-pointer hover:text-[#BE0F12] transition-colors whitespace-normal"
                  >
                    <span className="pr-2 leading-snug">Hvornår er det nødvendigt at skifte taget på min bolig?</span>
                    <i className={`fa-solid fa-chevron-down text-[13px] shrink-0 transition-transform duration-200 ${openRoofFaq === 2 ? 'rotate-180 text-[#BE0F12]' : 'text-neutral-500'}`}></i>
                  </button>
                  {openRoofFaq === 2 && (
                    <div className="px-4 pb-4 pt-1 font-['Open_Sans'] text-[13px] sm:text-[13.5px] text-[#363636] space-y-2 leading-relaxed border-t border-neutral-300 bg-white">
                      <p className="pt-2">
                        Tegn på at taget bør udskiftes er utætheder, fugt på loftet, revnede tagsten/plader, et nedslidt undertag eller hvis taget har overskredet sin tekniske levetid (f.eks. ældre asbesttage).
                      </p>
                    </div>
                  )}
                </div>

                {/* Tag FAQ 4 */}
                <div className="bg-[#EDEDED] border border-neutral-300/60 transition-colors">
                  <button
                    type="button"
                    onClick={() => toggleRoofFaq(3)}
                    className="w-full py-5 min-h-[74px] text-left px-5 sm:px-6 text-[#2E2E2E] font-['Saira'] font-semibold text-[15px] sm:text-[16px] lg:text-[16.5px] flex items-center justify-between gap-3 cursor-pointer hover:text-[#BE0F12] transition-colors whitespace-normal"
                  >
                    <span className="pr-2 leading-snug">Hvilke fordele er der ved at få nyt tag?</span>
                    <i className={`fa-solid fa-chevron-down text-[13px] shrink-0 transition-transform duration-200 ${openRoofFaq === 3 ? 'rotate-180 text-[#BE0F12]' : 'text-neutral-500'}`}></i>
                  </button>
                  {openRoofFaq === 3 && (
                    <div className="px-4 pb-4 pt-1 font-['Open_Sans'] text-[13px] sm:text-[13.5px] text-[#363636] space-y-2 leading-relaxed border-t border-neutral-300 bg-white">
                      <p className="pt-2">
                        Et nyt tag beskytter dit hus mod fugtskader, forbedrer energieffektiviteten (især kombineret med isolering), løfter husets æstetik markant og øger ejendommens samlede handelsværdi.
                      </p>
                    </div>
                  )}
                </div>

                {/* Tag FAQ 5 */}
                <div className="bg-[#EDEDED] border border-neutral-300/60 transition-colors">
                  <button
                    type="button"
                    onClick={() => toggleRoofFaq(4)}
                    className="w-full py-5 min-h-[74px] text-left px-5 sm:px-6 text-[#2E2E2E] font-['Saira'] font-semibold text-[15px] sm:text-[16px] lg:text-[16.5px] flex items-center justify-between gap-3 cursor-pointer hover:text-[#BE0F12] transition-colors whitespace-normal"
                  >
                    <span className="pr-2 leading-snug">Hvor lang tid tager det at lave et nyt tag?</span>
                    <i className={`fa-solid fa-chevron-down text-[13px] shrink-0 transition-transform duration-200 ${openRoofFaq === 4 ? 'rotate-180 text-[#BE0F12]' : 'text-neutral-500'}`}></i>
                  </button>
                  {openRoofFaq === 4 && (
                    <div className="px-4 pb-4 pt-1 font-['Open_Sans'] text-[13px] sm:text-[13.5px] text-[#363636] space-y-2 leading-relaxed border-t border-neutral-300 bg-white">
                      <p className="pt-2">
                        En standard tagudskiftning på et enfamiliehus tager typisk 2-4 uger afhængigt af vejret, tagets størrelse og om der skal udføres asbestsanering eller efterisolering.
                      </p>
                    </div>
                  )}
                </div>

              </div>
            </div>

            {/* Kolonne 2: Energiløsninger (48.5% bredde) - distance til første række øget ~4px (mb-5.5) */}
            <div className="w-full">
              <h3 className="font-['Saira'] font-bold text-base sm:text-lg text-[#2E2E2E] mb-5.5 pb-1 border-b border-neutral-200">
                Energiløsninger
              </h3>

              <div className="space-y-[6px]">
                
                {/* Energi FAQ 1 - højde øget ~2px (min-h-[74px]) */}
                <div className="bg-[#EDEDED] border border-neutral-300/60 transition-colors">
                  <button
                    type="button"
                    onClick={() => toggleEnergyFaq(0)}
                    className="w-full py-5 min-h-[74px] text-left px-5 sm:px-6 text-[#2E2E2E] font-['Saira'] font-semibold text-[15px] sm:text-[16px] lg:text-[16.5px] flex items-center justify-between gap-3 cursor-pointer hover:text-[#BE0F12] transition-colors whitespace-normal"
                  >
                    <span className="pr-2 leading-snug">Hvad koster det at få efterisoleret?</span>
                    <i className={`fa-solid fa-chevron-down text-[13px] shrink-0 transition-transform duration-200 ${openEnergyFaq === 0 ? 'rotate-180 text-[#BE0F12]' : 'text-neutral-500'}`}></i>
                  </button>
                  {openEnergyFaq === 0 && (
                    <div className="px-4 pb-4 pt-1 font-['Open_Sans'] text-[13px] sm:text-[13.5px] text-[#363636] space-y-2 leading-relaxed border-t border-neutral-300 bg-white">
                      <p className="pt-2">
                        Efterisolering af loft koster typisk mellem 150 og 350 kr. pr. m² afhængigt af den eksisterende isolering og adgangsforhold. Det er en af de mest rentable energiforbedringer med hurtig tilbagebetalingstid.
                      </p>
                    </div>
                  )}
                </div>

                {/* Energi FAQ 2 */}
                <div className="bg-[#EDEDED] border border-neutral-300/60 transition-colors">
                  <button
                    type="button"
                    onClick={() => toggleEnergyFaq(1)}
                    className="w-full py-5 min-h-[74px] text-left px-5 sm:px-6 text-[#2E2E2E] font-['Saira'] font-semibold text-[15px] sm:text-[16px] lg:text-[16.5px] flex items-center justify-between gap-3 cursor-pointer hover:text-[#BE0F12] transition-colors whitespace-normal"
                  >
                    <span className="pr-2 leading-snug">Hvorfor er det en god idé at få efterisoleret sin bolig?</span>
                    <i className={`fa-solid fa-chevron-down text-[13px] shrink-0 transition-transform duration-200 ${openEnergyFaq === 1 ? 'rotate-180 text-[#BE0F12]' : 'text-neutral-500'}`}></i>
                  </button>
                  {openEnergyFaq === 1 && (
                    <div className="px-4 pb-4 pt-1 font-['Open_Sans'] text-[13px] sm:text-[13.5px] text-[#363636] space-y-2 leading-relaxed border-t border-neutral-300 bg-white">
                      <p className="pt-2">
                        Du opnår en markant lavere varmeregning, et markant forbedret indeklima uden træk og kuldebroer, og du reducerer boligens CO2-aftryk, samtidig med at boligens energimærke forbedres.
                      </p>
                    </div>
                  )}
                </div>

                {/* Energi FAQ 3 */}
                <div className="bg-[#EDEDED] border border-neutral-300/60 transition-colors">
                  <button
                    type="button"
                    onClick={() => toggleEnergyFaq(2)}
                    className="w-full py-5 min-h-[74px] text-left px-5 sm:px-6 text-[#2E2E2E] font-['Saira'] font-semibold text-[15px] sm:text-[16px] lg:text-[16.5px] flex items-center justify-between gap-3 cursor-pointer hover:text-[#BE0F12] transition-colors whitespace-normal"
                  >
                    <span className="pr-2 leading-snug">Hvorfor skal jeg skifte boligens vinduer/døre?</span>
                    <i className={`fa-solid fa-chevron-down text-[13px] shrink-0 transition-transform duration-200 ${openEnergyFaq === 2 ? 'rotate-180 text-[#BE0F12]' : 'text-neutral-500'}`}></i>
                  </button>
                  {openEnergyFaq === 2 && (
                    <div className="px-4 pb-4 pt-1 font-['Open_Sans'] text-[13px] sm:text-[13.5px] text-[#363636] space-y-2 leading-relaxed border-t border-neutral-300 bg-white">
                      <p className="pt-2">
                        Gamle vinduer og døre er ofte den største kilde til varmetab. Nye 3-lags energiruder minimerer træk, dæmper støj udefra og sparer mange tusinde kroner årligt på opvarmningen.
                      </p>
                    </div>
                  )}
                </div>

                {/* Energi FAQ 4 */}
                <div className="bg-[#EDEDED] border border-neutral-300/60 transition-colors">
                  <button
                    type="button"
                    onClick={() => toggleEnergyFaq(3)}
                    className="w-full py-5 min-h-[74px] text-left px-5 sm:px-6 text-[#2E2E2E] font-['Saira'] font-semibold text-[15px] sm:text-[16px] lg:text-[16.5px] flex items-center justify-between gap-3 cursor-pointer hover:text-[#BE0F12] transition-colors whitespace-normal"
                  >
                    <span className="pr-2 leading-snug">Hvordan påvirker mit nye tag varmeforbruget i min bolig?</span>
                    <i className={`fa-solid fa-chevron-down text-[13px] shrink-0 transition-transform duration-200 ${openEnergyFaq === 3 ? 'rotate-180 text-[#BE0F12]' : 'text-neutral-500'}`}></i>
                  </button>
                  {openEnergyFaq === 3 && (
                    <div className="px-4 pb-4 pt-1 font-['Open_Sans'] text-[13px] sm:text-[13.5px] text-[#363636] space-y-2 leading-relaxed border-t border-neutral-300 bg-white">
                      <p className="pt-2">
                        Op mod 30% af boligens varme kan forsvinde ud gennem et utæt eller dårligt isoleret tag. Et nyt tag med tidssvarende undertag og merisolering kan reducere varmetabet markant fra dag ét.
                      </p>
                    </div>
                  )}
                </div>

                {/* Energi FAQ 5 */}
                <div className="bg-[#EDEDED] border border-neutral-300/60 transition-colors">
                  <button
                    type="button"
                    onClick={() => toggleEnergyFaq(4)}
                    className="w-full py-5 min-h-[74px] text-left px-5 sm:px-6 text-[#2E2E2E] font-['Saira'] font-semibold text-[15px] sm:text-[16px] lg:text-[16.5px] flex items-center justify-between gap-3 cursor-pointer hover:text-[#BE0F12] transition-colors whitespace-normal"
                  >
                    <span className="pr-2 leading-snug">Hvad er kravene til energioptimering i dag?</span>
                    <i className={`fa-solid fa-chevron-down text-[13px] shrink-0 transition-transform duration-200 ${openEnergyFaq === 4 ? 'rotate-180 text-[#BE0F12]' : 'text-neutral-500'}`}></i>
                  </button>
                  {openEnergyFaq === 4 && (
                    <div className="px-4 pb-4 pt-1 font-['Open_Sans'] text-[13px] sm:text-[13.5px] text-[#363636] space-y-2 leading-relaxed border-t border-neutral-300 bg-white">
                      <p className="pt-2">
                        Bygningsreglementet (BR18) stiller krav om, at man ved større renoveringer som tagudskiftning skal efterisolere, hvis det er rentabelt. Vi sikrer altid, at dit projekt overholder alle gældende energikrav.
                      </p>
                    </div>
                  )}
                </div>

              </div>
            </div>

          </div>

        </div>
      </section>

      {/* ============================================================ */}
      {/* 7. QUALITY / CRAFTSMANSHIP SECTION (POSITION LÅST)          */}
      {/* ============================================================ */}
      <section id="kvalitet" className="pt-12 pb-4 lg:pt-[80px] lg:pb-6 bg-white">
        <div 
          className="w-full mx-auto px-4 sm:px-6 lg:px-0"
          style={{ width: 'clamp(820px, 62vw, 1180px)', maxWidth: '100%' }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 items-start" style={{ gap: '3%' }}>
            
            {/* Tekstindhold venstre (48.5% bredde) - placeret nær toppen af det høje billede */}
            <div className="space-y-4 pt-1">
              <h2 className="font-['Saira'] font-bold text-[28px] sm:text-[35px] lg:text-[38px] text-[#2E2E2E] tracking-tight leading-tight">
                Kompromisløst håndværk<br />til tiden
              </h2>
              
              <div className="space-y-3 font-['Open_Sans'] text-[16.5px] sm:text-[17px] text-[#363636] leading-[1.7]">
                <p>
                  Der er mange faktorer at tage stilling til, når man skal have nyt tag, og det kan være en jungle at sætte sig ind i forskellige tagtyper, materialer osv. Hos Dansk Tagbyg vil vi skabe en så tryg og gennemsigtig proces som muligt, så du kan tage den bedste beslutning.
                </p>
                <p>
                  Vi arbejder tæt sammen med førende producenter som Swisspearl på baggrund af vores høje faglighed og dedikation til at levere kvalitetshåndværk og yde en sublim kundeservice. Det betyder, at du kan stole på, at vi aldrig går på kompromis med hverken håndværk, service eller materialevalg.
                </p>
              </div>

              {/* 2 pilleknapper: lodret stak, forøget bredde ~5-7% til 66% */}
              <div className="pt-4 flex flex-col items-start gap-2.5 w-full">
                <Link
                  to="/kontakt"
                  className="w-full sm:w-[66%] rounded-full bg-[#BE0F12] hover:bg-[#A30D0F] text-white px-8 py-3.5 font-semibold text-xs sm:text-[14.5px] lg:text-[15px] transition-colors block text-center"
                >
                  Kontakt os og få rådgivning
                </Link>
                <Link
                  to="/profil"
                  className="w-full sm:w-[66%] rounded-full border border-[#2E2E2E] bg-white text-[#2E2E2E] hover:bg-[#2E2E2E] hover:text-white px-8 py-3.5 font-semibold text-xs sm:text-[14.5px] lg:text-[15px] transition-colors block text-center"
                >
                  Læs mere om os
                </Link>
              </div>
            </div>

            {/* Højportræt foto højre (48.5% bredde, aspect 0.88:1, -3.5% højde, rækker synligt lavere end teksten) */}
            <div className="w-full aspect-[0.88/1] overflow-hidden flex-shrink-0 bg-neutral-100 rounded-none shadow-none">
              <img
                src="/_assets/imgi-8-thumb.jpg"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = 'https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/imgi-8-thumb.jpg';
                }}
                alt="Kompromisløst håndværk til tiden"
                loading="lazy"
                className="w-full h-full object-cover block"
              />
            </div>

          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* 8. ASBESTOS / SECOND FEATURE SECTION (flyttet ~3px ned for flow-kompensation: -mt-[15px]) */}
      {/* ============================================================ */}
      <section id="asbest" className="pt-6 pb-12 lg:pt-[44px] lg:pb-[66px] bg-white -mt-[15px]">
        <div 
          className="w-full mx-auto px-4 sm:px-6 lg:px-0"
          style={{ width: 'clamp(820px, 62vw, 1180px)', maxWidth: '100%' }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 items-center" style={{ gap: '3%' }}>
            
            {/* Kvadratisk asbestbillede venstre (48.5% bredde, aspect 1.03:1, -3% højde, badge fuldt bevaret uden beskæring) */}
            <div className="w-full aspect-[1.03/1] overflow-hidden flex-shrink-0 bg-neutral-100 rounded-none shadow-none">
              <img
                src="/_assets/scraped-1_590868ff.webp"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = 'https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/scraped-1_590868ff.webp';
                }}
                alt="Asbest autoriseret tagfirma"
                loading="lazy"
                className="w-full h-full object-cover object-left block"
              />
            </div>

            {/* Tekst højre (48.5% bredde, vertikalt centreret mod det kvadratiske billede) */}
            <div className="space-y-4">
              <h2 className="font-['Saira'] font-bold text-[28px] sm:text-[35px] lg:text-[38px] text-[#2E2E2E] tracking-tight leading-tight">
                Har du et asbesttag,<br />der skal fjernes?
              </h2>

              <div className="space-y-3 font-['Open_Sans'] text-[16.5px] sm:text-[17px] text-[#363636] leading-[1.7]">
                <p>
                  Vidste du, at det siden 1. januar 2025 har været et krav i arbejdsmiljølovgivningen, at nedrivning og sanering af asbestholdige materialer – som fx eternittage – kun må udføres af virksomheder, der er autoriseret af Arbejdstilsynet?
                </p>
                <p>
                  Hos Dansk Tagbyg har vi den lovpligtige autorisation og en fast procedure for sikker og korrekt håndtering af asbest. Det betyder, at du er garanteret et stykke arbejde, der lever op til alle krav i dansk lovgivning – og samtidig undgår du bøder og andre alvorlige konsekvenser ved selv at stå for nedtagningen af et asbesttag.
                </p>
              </div>

              <div className="pt-3">
                <Link
                  to="/asbest-autoriseret"
                  className="w-full sm:w-[66%] rounded-full bg-[#BE0F12] hover:bg-[#A30D0F] text-white px-8 py-3.5 font-semibold text-[15px] sm:text-[15.5px] transition-colors block text-center"
                >
                  Læs mere her
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}
